/**
 * Global Sync using Firebase Firestore - NO API KEY NEEDED!
 * Uses your existing Firebase setup for global sync across ALL devices
 */

import {
  getCategories as firestoreGetCategories,
  getSubcategories as firestoreGetSubcategories,
  getContactInfo as firestoreGetContactInfo,
  addCategory as firestoreAddCategory,
  updateCategory as firestoreUpdateCategory,
  deleteCategory as firestoreDeleteCategory,
  addSubcategory as firestoreAddSubcategory,
  updateSubcategory as firestoreUpdateSubcategory,
  deleteSubcategory as firestoreDeleteSubcategory,
  updateContactInfo as firestoreUpdateContactInfo,
} from './firestoreService';

// Helper to get from localStorage (fallback)
const getFromStorage = (key, defaultValue = []) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

// Helper to save to localStorage (backup)
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    return false;
  }
};

// Check if Firebase is configured - always try since config has fallbacks
const isFirebaseConfigured = () => {
  // Firebase config has fallback values, so always try to use Firebase
  // Only skip if explicitly disabled
  return process.env.REACT_APP_USE_FIREBASE !== 'false';
};

export const apiService = {
  // Categories
  async getCategories() {
    try {
      if (isFirebaseConfigured()) {
        const data = await firestoreGetCategories();
        // Backup to localStorage
        saveToStorage('Categories', data);
        return data;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    return getFromStorage('Categories', []);
  },

  async addCategory(category) {
    const newCategory = {
      ...category,
      id: category.id || Date.now().toString(),
      created_at: category.created_at || new Date().toISOString(),
    };

    // ALWAYS try Firebase first
    try {
      if (isFirebaseConfigured()) {
        console.log('🔥 Saving to Firebase Firestore...', newCategory);
        const result = await firestoreAddCategory(newCategory);
        console.log('✅ Saved to Firebase!', result);
        // Backup to localStorage
        const categories = getFromStorage('Categories', []);
        categories.push(result);
        saveToStorage('Categories', categories);
        return result;
      }
    } catch (error) {
      console.error('❌ Firebase error:', error);
      console.error('Error details:', error.message, error.code);
      // Show error to user
      alert(`Firebase Error: ${error.message}. Check browser console for details.`);
    }
    
    // Fallback to localStorage
    console.warn('⚠️ Using localStorage fallback');
    const categories = getFromStorage('Categories', []);
    categories.push(newCategory);
    saveToStorage('Categories', categories);
    return newCategory;
  },

  async updateCategory(id, category) {
    const updatedCategory = { ...category, id };

    try {
      if (isFirebaseConfigured()) {
        const result = await firestoreUpdateCategory(id, updatedCategory);
        // Backup to localStorage
        const categories = getFromStorage('Categories', []);
        const index = categories.findIndex(cat => String(cat.id) === String(id));
        if (index !== -1) {
          categories[index] = result;
        } else {
          categories.push(result);
        }
        saveToStorage('Categories', categories);
        return result;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    // Fallback to localStorage
    const categories = getFromStorage('Categories', []);
    const index = categories.findIndex(cat => String(cat.id) === String(id));
    if (index !== -1) {
      categories[index] = updatedCategory;
    } else {
      categories.push(updatedCategory);
    }
    saveToStorage('Categories', categories);
    return updatedCategory;
  },

  async deleteCategory(id) {
    try {
      if (isFirebaseConfigured()) {
        await firestoreDeleteCategory(id);
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    // Also delete from localStorage
    const categories = getFromStorage('Categories', []);
    const filtered = categories.filter(cat => String(cat.id) !== String(id));
    saveToStorage('Categories', filtered);
    
    // Also delete related subcategories
    const subcategories = getFromStorage('Subcategories', []);
    const filteredSubs = subcategories.filter(sub => String(sub.category_id) !== String(id));
    saveToStorage('Subcategories', filteredSubs);
    
    return true;
  },

  // Subcategories
  async getSubcategories() {
    try {
      if (isFirebaseConfigured()) {
        const data = await firestoreGetSubcategories();
        saveToStorage('Subcategories', data);
        return data;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    return getFromStorage('Subcategories', []);
  },

  async addSubcategory(subcategory) {
    const newSubcategory = {
      ...subcategory,
      id: subcategory.id || Date.now().toString(),
      created_at: subcategory.created_at || new Date().toISOString(),
    };

    try {
      if (isFirebaseConfigured()) {
        const result = await firestoreAddSubcategory(newSubcategory);
        const subcategories = getFromStorage('Subcategories', []);
        subcategories.push(result);
        saveToStorage('Subcategories', subcategories);
        return result;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    const subcategories = getFromStorage('Subcategories', []);
    subcategories.push(newSubcategory);
    saveToStorage('Subcategories', subcategories);
    return newSubcategory;
  },

  async updateSubcategory(id, subcategory) {
    const updatedSubcategory = { ...subcategory, id };

    try {
      if (isFirebaseConfigured()) {
        const result = await firestoreUpdateSubcategory(id, updatedSubcategory);
        const subcategories = getFromStorage('Subcategories', []);
        const index = subcategories.findIndex(sub => String(sub.id) === String(id));
        if (index !== -1) {
          subcategories[index] = result;
        } else {
          subcategories.push(result);
        }
        saveToStorage('Subcategories', subcategories);
        return result;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    const subcategories = getFromStorage('Subcategories', []);
    const index = subcategories.findIndex(sub => String(sub.id) === String(id));
    if (index !== -1) {
      subcategories[index] = updatedSubcategory;
    } else {
      subcategories.push(updatedSubcategory);
    }
    saveToStorage('Subcategories', subcategories);
    return updatedSubcategory;
  },

  async deleteSubcategory(id) {
    try {
      if (isFirebaseConfigured()) {
        await firestoreDeleteSubcategory(id);
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    const subcategories = getFromStorage('Subcategories', []);
    const filtered = subcategories.filter(sub => String(sub.id) !== String(id));
    saveToStorage('Subcategories', filtered);
    return true;
  },

  // Contact Info
  async getContactInfo() {
    try {
      if (isFirebaseConfigured()) {
        const data = await firestoreGetContactInfo();
        if (data) {
          saveToStorage('ContactInfo', data);
        }
        return data;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    return getFromStorage('ContactInfo', null);
  },

  async updateContactInfo(contactInfo) {
    try {
      if (isFirebaseConfigured()) {
        const result = await firestoreUpdateContactInfo(contactInfo);
        saveToStorage('ContactInfo', result);
        return result;
      }
    } catch (error) {
      console.log('Firestore not available, using localStorage:', error.message);
    }
    
    saveToStorage('ContactInfo', contactInfo);
    return contactInfo;
  },
};
